import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { InterventionParams, LocationData } from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonOldComponent } from '../button-old';
import { MatCardModule } from '@angular/material/card';
import { PeriodPickerComponent } from '../period-picker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatOptionModule } from '@angular/material/core';
import { NgIf, NgFor } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

@Component({
  selector: 'sc-search-bar',
  styleUrls: ['./search-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <form [formGroup]="form" class="search-bar">
      <div class="form-column">
        <mat-form-field class="addressSearchInput">
          <mat-label>{{ 'SearchBar.addressInptLabel' | translate }}</mat-label>
          <input
            id="addressSearchInput"
            formControlName="address"
            (ngModelChange)="onInputChange(input.value)"
            [matAutocomplete]="auto"
            [value]="
              noAddressFound ? typedAddress : selectedGooglePlace?.address
            "
            matInput
            #input
          />

          <mat-autocomplete
            #auto="matAutocomplete"
            [displayWith]="displayPlace"
          >
            <ng-container *ngIf="!noAddressFound">
              <mat-option
                *ngFor="let googlePlace of googlePlaces; let index = index"
                [value]="googlePlace"
                (onSelectionChange)="onSelectAddress(googlePlace)"
                [id]="index + 'addressSearchOption'"
              >
                <span>{{ googlePlace.address }}</span>
              </mat-option>
            </ng-container>
          </mat-autocomplete>
          <mat-hint *ngIf="addressCompleteRequired">
            <p>{{ 'Intervention.error.addressIncomplete' | translate }}</p>
          </mat-hint>
        </mat-form-field>
      </div>
      <div class="form-column" *ngIf="noAddressFound">
        <mat-form-field *ngIf="displayAutocomplete">
          <mat-label>{{
            'SearchBar.postaCodeInputTitle' | translate
          }}</mat-label>
          <input
            formControlName="addressPostal"
            (ngModelChange)="
              onPostalCodeInputChange(inputPostalCodeAutocomplete.value)
            "
            minLength="5"
            maxLength="5"
            matInput
            [value]="getPostalCodeValue(false)"
            [matAutocomplete]="postalCodeAC"
            #inputPostalCodeAutocomplete
          />
          <mat-autocomplete
            autoActiveFirstOption
            #postalCodeAC="matAutocomplete"
          >
            <mat-option
              *ngFor="let option of placesByPostalCode"
              [value]="option.postcode"
              (onSelectionChange)="onSelectPlace(option)"
            >
              {{ option.label }}
            </mat-option>
          </mat-autocomplete>
          <span
            matSuffix
            *ngIf="postalCode"
            [matTooltip]="getPostalCodeValue(true)"
            ><span>{{
              inputPostalCodeAutocomplete.value ? getPostalCodeValue(true) : ''
            }}</span></span
          >
        </mat-form-field>
        <mat-form-field *ngIf="!displayAutocomplete">
          <mat-label>{{
            'SearchBar.postaCodeInputTitle' | translate
          }}</mat-label>
          <input
            formControlName="addressPostal"
            (ngModelChange)="onPostalCodeInputChange(inputPostalCode.value)"
            minLength="5"
            maxLength="5"
            matInput
            [value]="getPostalCodeValue(false)"
            #inputPostalCode
          />
          <span
            matSuffix
            *ngIf="postalCode"
            [matTooltip]="getPostalCodeValue(true)"
            ><span>{{
              inputPostalCode.value ? getPostalCodeValue(true) : ''
            }}</span></span
          >
        </mat-form-field>
      </div>

      <div class="form-column period">
        <sc-period-picker
          [_initValue]="periodPickerInitValue"
          [label]="'SearchBar.interventionDateLabel' | translate"
          [width]="190"
          (validateDatesEvent)="onValidateDates($event)"
        ></sc-period-picker>
      </div>

      <div class="form-column sc-form-field" id="options">
        <mat-form-field>
          <mat-label>{{ 'SearchBar.selectedOptions' | translate }}</mat-label>
          <input
            autocomplete="off"
            formControlName="options"
            matInput
            #input
            readonly
            (click)="onInputClick()"
          />
        </mat-form-field>
        <mat-card
          appearance="outlined"
          class="option-list"
          *ngIf="displayOptions"
          (click)="onInputClick()"
        >
          <div>
            <mat-radio-group
              formControlName="interventionType"
              (change)="interventionOptionsChange($event)"
            >
              <mat-radio-button
                id="isNormalInterventionSearchRadioButton"
                value="isNormalIntervention"
              >
                {{
                  !disabledReplacementVehicle
                    ? ('SearchBar.inFitterWithoutCarChoice' | translate)
                    : ('SearchBar.inFitter' | translate)
                }}</mat-radio-button
              >
              <mat-radio-button
                id="withReplacementVehicleSearchRadioButton"
                value="withReplacementVehicle"
                *ngIf="!disabledReplacementVehicle"
              >
                {{
                  'SearchBar.inFitterWithCarChoice' | translate
                }}</mat-radio-button
              >
              <mat-radio-button
                id="isOutsideInterventionSearchRadioButton"
                *ngIf="!disabledInMobilityService"
                value="isOutsideIntervention"
              >
                {{ 'SearchBar.atHomeChoice' | translate }}</mat-radio-button
              >
            </mat-radio-group>
          </div>
        </mat-card>
      </div>

      <sc-button-old
        id="addressSearchButton"
        [text]="'SearchBar.searchBtn' | translate"
        widthInPx="176"
        [disabled]="
          form.invalid ||
          !selectedGooglePlace?.placeId ||
          addressCompleteRequired
        "
        [buttonType]="
          form.valid && selectedGooglePlace?.placeId && !addressCompleteRequired
            ? 'PRIMARY'
            : 'DISABLED'
        "
        (buttonClick)="onSearchClick()"
      ></sc-button-old>
    </form>
  `,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    NgIf,
    NgFor,
    MatOptionModule,
    MatTooltipModule,
    PeriodPickerComponent,
    MatCardModule,
    MatRadioModule,
    ButtonOldComponent,
    TranslateModule,
  ],
})
export class SearchBarComponent implements OnInit, OnChanges {
  @ViewChild('inputPostalCodeAutocomplete') postalCodeMatInput: ElementRef;
  @Input() initAddress: LocationData = null;
  @Input() googlePlaces: LocationData[] = [];

  @Input() periodPickerInitValue: { startDate: Date; endDate: Date };
  @Input() isLoading = false;
  @Input() disabledReplacementVehicle = false;
  @Input() disabledInMobilityService = false;
  @Input() defaultInterventionDuration: string;
  @Input() placesByPostalCode: any[];
  @Input() hidePostalCodeInput = false;
  @Input() postalCode: LocationData = null;
  @Input() typedAddress;
  @Input() noAddressFound = false;

  @Input() interventionParams: InterventionParams = {
    fitterIntervention: true,
    outsideIntervention: false,
    replacementVehicle: false,
    displayGeneralists: false,
    displayGlassSpecialist: true,
  };
  @Output()
  interventionParamsChoiceChangeEvent = new EventEmitter<InterventionParams>();
  @Output() searchButtonClickEvent = new EventEmitter<void>();
  @Output() inputChangeEvent = new EventEmitter<string>();
  @Output() selectAddressEvent = new EventEmitter<LocationData>();
  @Output() validateDateEvent = new EventEmitter<{
    startDate: Date;
    endDate: Date;
  }>();
  @Output() deletedAddressEvent = new EventEmitter<void>();
  @Output() moveToCoordinate = new EventEmitter<any>();
  @Output() postalCodeInputChangeEvent = new EventEmitter<string>();

  public form: FormGroup;
  displayOptions = false;
  listOptionsClicked = false;
  typingTimer = null;
  selectedGooglePlace: LocationData = null;
  displayAutocomplete = false;
  addressCompleteRequired = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    public elementRef: ElementRef,
    private readonly translator: TranslatorService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly ffmService: FeatureFlippingManagerService
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      address: [this.selectedGooglePlace, Validators.required],
      addressPostal: [''],
      searchDates: [null, Validators.required],
      options: [''],
      interventionType: [this.initInterventionType()],
    });
    this.updateOptionsLabelFormValue();

    this.changeDetectorRef.detectChanges();
    if (
      this.ffmService.isFeatureActivated('display-autocomplete-for-postal-code')
    ) {
      this.displayAutocomplete = true;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['defaultInterventionDuration']?.previousValue) {
      this.defaultInterventionDuration =
        changes['defaultInterventionDuration'].currentValue;
      this.updateOptionsLabelFormValue();
      this.changeDetectorRef.detectChanges();
    }

    if (changes['interventionParams']?.currentValue && this.form) {
      this.interventionParams = changes['interventionParams']?.currentValue;
      this.form.get('interventionType').setValue(this.initInterventionType());
      this.updateOptionsLabelFormValue();
    }
    if (changes['disabledReplacementVehicle']?.currentValue && this.form) {
      this.disabledReplacementVehicle =
        changes['disabledReplacementVehicle']?.currentValue;
      this.form.get('interventionType').setValue(this.initInterventionType());
      this.updateOptionsLabelFormValue();
    }
    if (changes['disabledInMobilityService']?.currentValue && this.form) {
      this.disabledInMobilityService =
        changes['disabledInMobilityService']?.currentValue;
      this.form.get('interventionType').setValue(this.initInterventionType());
      this.updateOptionsLabelFormValue();
    }
    if (
      changes['initAddress']?.currentValue &&
      changes['initAddress']?.currentValue?.placeId !==
        this.selectedGooglePlace?.placeId
    ) {
      this.onSelectAddress(changes['initAddress']?.currentValue);
      this.form.get('address').setValue(changes['initAddress']?.currentValue);
      this.changeDetectorRef.detectChanges();
    }
    if (changes['postalCode']?.currentValue && this.form) {
      const currentPostalCode = changes['postalCode']?.currentValue;
      if (!currentPostalCode.address && this.form?.value)
        this.form.value['addressPostal'] = '';
      if (!currentPostalCode.address && this.postalCodeMatInput)
        this.postalCodeMatInput.nativeElement.value = '';
    }
  }

  onInputClick(): void {
    this.listOptionsClicked = true;
    this.changeDetectorRef.detectChanges();
  }

  getPostalCodeValue(isSuffix: boolean) {
    if (this.postalCode) {
      if (
        !this.selectedGooglePlace ||
        this.selectedGooglePlace?.placeId !== this.postalCode?.placeId
      ) {
        this.selectedGooglePlace = this.postalCode;
        this.selectAddressEvent.emit(this.postalCode);
      }
      const postalCodeArray = this.postalCode?.address?.split(',');
      try {
        return isSuffix
          ? postalCodeArray[0].slice(
              postalCodeArray[0].indexOf(' '),
              postalCodeArray[0].length
            )
          : postalCodeArray[0]?.split(' ')[0];
      } catch (e) {
        return '';
      }
    }
    return '';
  }

  onSelectPlace(value) {
    this.postalCode = {
      address: value.postcode + ',' + value.label,
      placeId: null,
      isKnownAddress: false,
      isCompleteAddress: null,
      locationProvider: null,
    };
  }

  onInputChange(value: string): void {
    if (!value || value === '' || this.selectedGooglePlace?.address !== value) {
      this.selectedGooglePlace = null;
      this.deletedAddressEvent.emit();
    }

    if (this.typedAddress !== value) {
      this.typedAddress = value;
      if (this.typingTimer) {
        this.selectedGooglePlace = null;
        clearTimeout(this.typingTimer);
      }
      this.typingTimer = setTimeout(() => {
        this.typingTimer = null;
        this.noAddressFound = false;
        this.postalCode = null;
        this.form.get('address').setValidators([Validators.required]);
        this.form.get('addressPostal').clearValidators();
        this.inputChangeEvent.emit(value);
      }, 400);
    }
  }

  onPostalCodeInputChange(value) {
    if (this.form.get('addressPostal').valid) {
      if (!value || value === '') {
        this.deletedAddressEvent.emit();
      }

      if (this.typingTimer) {
        this.selectedGooglePlace = null;
        clearTimeout(this.typingTimer);
      }

      this.typingTimer = setTimeout(() => {
        this.typingTimer = null;
        this.postalCodeInputChangeEvent.emit(value);
      }, 400);
    }
  }

  onSelectAddress(value: LocationData): void {
    if (value.placeId) {
      this.selectedGooglePlace = value;
      if (this.form?.value['interventionType'] === 'isOutsideIntervention') {
        this.addressCompleteRequired = !value?.isCompleteAddress;
      }
      this.selectAddressEvent.emit(value);
      this.changeDetectorRef.detectChanges();
    } else {
      this.noAddressFound = true;
      this.form.get('address').clearValidators();
      this.form.get('addressPostal').setValidators([Validators.required]);
      this.changeDetectorRef.detectChanges();
    }
  }

  onValidateDates(selectedDates: { startDate: Date; endDate: Date }): void {
    this.form.get('searchDates').setValue(selectedDates, { emitEvent: false });
    this.validateDateEvent.emit(selectedDates);
  }

  interventionOptionsChange(event: MatRadioChange) {
    if (event) {
      this.form.get('interventionType').setValue(event.value);
      this.displayOptions = false;
      switch (event.value) {
        case 'isOutsideIntervention': {
          this.interventionParams = {
            outsideIntervention: true,
            replacementVehicle: false,
            fitterIntervention: false,
            displayGeneralists: false,
            displayGlassSpecialist: true,
          };
          if (this.form.value['address']) {
            this.addressCompleteRequired =
              !this.form.value['address']?.isCompleteAddress;
          }
          break;
        }
        case 'isNormalIntervention': {
          this.interventionParams = {
            outsideIntervention: false,
            replacementVehicle: false,
            fitterIntervention: true,
            displayGeneralists: false,
            displayGlassSpecialist: true,
          };

          this.addressCompleteRequired = false;

          break;
        }
        case 'withReplacementVehicle': {
          this.interventionParams = {
            outsideIntervention: false,
            replacementVehicle: true,
            fitterIntervention: true,
            displayGeneralists: false,
            displayGlassSpecialist: true,
          };

          this.addressCompleteRequired = false;
          break;
        }
      }
      this.updateOptionsLabelFormValue();
      this.interventionParamsChoiceChangeEvent.emit(this.interventionParams);
    }
  }

  initInterventionType() {
    if (
      !this.interventionParams?.outsideIntervention &&
      this.interventionParams?.replacementVehicle &&
      this.interventionParams?.fitterIntervention &&
      !this.disabledReplacementVehicle
    ) {
      return 'withReplacementVehicle';
    } else if (
      !this.interventionParams?.outsideIntervention &&
      !this.interventionParams?.replacementVehicle &&
      this.interventionParams?.fitterIntervention &&
      this.disabledReplacementVehicle
    ) {
      return 'isNormalIntervention';
    } else if (
      this.interventionParams?.outsideIntervention &&
      !this.interventionParams?.replacementVehicle &&
      !this.interventionParams?.fitterIntervention &&
      !this.disabledInMobilityService
    ) {
      return 'isOutsideIntervention';
    } else {
      return 'isNormalIntervention';
    }
  }

  updateOptionsLabelFormValue(): void {
    let formValue;
    if (
      this.interventionParams?.outsideIntervention &&
      !this.disabledInMobilityService &&
      !this.interventionParams.fitterIntervention
    ) {
      formValue = this.translator.getInstantTranslation('SearchBar.atHome');
    } else if (
      this.interventionParams?.replacementVehicle &&
      this.interventionParams?.fitterIntervention &&
      !this.disabledReplacementVehicle &&
      !this.interventionParams?.outsideIntervention
    ) {
      formValue = !this.disabledReplacementVehicle
        ? this.translator.getInstantTranslation(
            'SearchBar.inFitterWithCarChoice'
          )
        : this.translator.getInstantTranslation('SearchBar.inFitter');
    } else if (
      (!this.interventionParams?.replacementVehicle &&
        this.interventionParams?.fitterIntervention) ||
      (this.interventionParams?.replacementVehicle &&
        this.interventionParams?.fitterIntervention &&
        this.interventionParams?.outsideIntervention)
    ) {
      formValue = !this.disabledReplacementVehicle
        ? this.translator.getInstantTranslation(
            'SearchBar.inFitterWithoutCarChoice'
          )
        : this.translator.getInstantTranslation('SearchBar.inFitter');
    }
    this.form.get('options').setValue(formValue);
  }

  onSearchClick(): void {
    this.searchButtonClickEvent.emit();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.displayOptions = this.listOptionsClicked;
    this.listOptionsClicked = false;
    this.changeDetectorRef.detectChanges();
  }

  displayPlace = (place: LocationData) => {
    if (place && place.placeId) {
      return place.address;
    } else return this.typedAddress;
  };
}
